import { FC, useEffect } from "react";

import { Box, Grid, Stack } from "@mui/material";
import { CampaignAction, getField, Ratio } from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { TeamData } from "@/components/CampaignModal/teamData";
import { CreateFundraisingProps } from "@/components/CreateFundraising/types";
import { ImageType, LegacyImageUploader } from "@/components/ImageUploader/LegacyImageUploader";
import { MAX_IMAGE_SIZES } from "@/constants/image";
import { useLocaleContext } from "@/contexts/LocaleContext";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";
import { FormAmountField } from "@/forms-design-system/FormAmountField";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormSelectField } from "@/forms-design-system/FormSelectField";
import { FormSelectItem } from "@/forms-design-system/FormSelectField/FormSelectItem";
import { FormTextField } from "@/forms-design-system/FormTextField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { trpc } from "@/helpers/trpc";
import { isEmail, isMax, isMin, isNameValid, isRequired } from "@/helpers/validators";
import { useTranslate } from "@/hooks/useTranslate";

export const FundraisingForm: FC<CreateFundraisingProps<TeamData>> = (props) => {
  const { errors, register, control, setValue, campaignAction, campaignId, teamId, defaultTarget, defaultTeamId } =
    props;
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const searchParams = useSearchParams();
  const amountMax = 1_000_000_000;

  const canJoinTeam = campaignAction === CampaignAction.JoinTeam && !teamId;

  const { data, isLoading: teamsLoading } = trpc.getDonationFormsFromCampaign.useQuery({
    campaignId: campaignId ?? "",
    filters: {
      page: 1,
      search: "",
      limit: 100,
    },
  });

  // Prefill form fields from URL parameters when there was a registration
  useEffect(() => {
    const firstName = searchParams.get("firstName");
    const lastName = searchParams.get("lastName");
    const email = searchParams.get("email");

    if (firstName) {
      setValue("firstName", firstName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (lastName) {
      setValue("lastName", lastName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (email) {
      setValue("email", email, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [searchParams, setValue]);

  // Set the team value to the formId if the campaignAction is JoinTeam
  useEffect(() => {
    if (campaignAction === CampaignAction.JoinTeam && defaultTeamId) {
      setValue("team", defaultTeamId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [defaultTeamId, campaignAction, setValue]);

  return (
    <Stack gap={2}>
      {campaignAction === CampaignAction.CreateTeam && (
        <FormTextField
          control={control}
          name="teamName"
          id="fundraising-team-name"
          placeholder={t("donationForm", "teamName")}
          label={t("donationForm", "teamName")}
          error={!!errors.teamName?.message}
          helperText={errors.teamName?.message}
          data-test="team-name-input"
          required
          autoFocus
        />
      )}
      {canJoinTeam && (
        <FormSelectField
          control={control}
          name="team"
          defaultValue={defaultTeamId}
          rules={[isRequired(canJoinTeam, t("dashboard", "common.isRequired"))]}
          label={t("donationForm", "joinTeam")}
          placeholder={t("donationForm", "selectYourTeam")}
          data-test="select-team-button"
          required
          renderValue={(value) => {
            const team = data?.team?.find((team) => team.id === value);

            return <span>{getField(team?.donationFormFields, locale)?.title}</span>;
          }}
        >
          {teamsLoading ? (
            <Grid container item justifyContent="center">
              <DashboardZeffyLoader />
            </Grid>
          ) : (
            data?.team?.map((team) => {
              return (
                <FormSelectItem data-test="team-menu-item" key={team.id} value={team.id}>
                  {getField(team.donationFormFields, locale)?.title}
                </FormSelectItem>
              );
            })
          )}
        </FormSelectField>
      )}
      <FormAmountField
        control={control}
        name="campaignTarget"
        defaultValue={defaultTarget}
        rules={[
          isRequired(t("donationForm", "targetIsRequired")),
          isMin(100, t("donationForm", "targetMin")),
          isMax(amountMax, t("dashboard", "common.isMax", { max: amountMax / 100 })),
        ]}
        id="fundraising-campaign-target"
        label={t("donationForm", "campaignTarget")}
        placeholder="0.00"
        required
        error={!!errors.campaignTarget?.message}
        helperText={errors.campaignTarget?.message}
        data-test="campaign-target-input"
      />
      <Stack direction={{ md: "row" }} alignItems={{ md: "center" }} gap={2}>
        <Box flex={{ md: "0 0 160px" }}>
          <LegacyImageUploader
            control={control}
            register={register}
            setValue={setValue}
            name="logo"
            importLabel={t("donationForm", "profilePhoto")}
            ratio={Ratio.Square}
            sizes={[100, 100]}
            maxDimensions={MAX_IMAGE_SIZES["logo"]}
            data-test="fundraising-logo-input"
            imageType={ImageType.Logo}
            disableCropOnMobile
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormTextField
              control={control}
              name="firstName"
              rules={[
                isRequired(t("donationForm", "firstNameRequired")),
                isNameValid(t("common", "invalidCharacterUsed")),
              ]}
              id="fundraising-first-name"
              label={t("donationForm", "firstName")}
              placeholder={t("donationForm", "firstName")}
              error={!!errors.firstName?.message}
              helperText={errors.firstName?.message}
              required
              data-test="first-name-input"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              control={control}
              name="lastName"
              rules={[
                isRequired(t("donationForm", "lastNameRequired")),
                isNameValid(t("common", "invalidCharacterUsed")),
              ]}
              id="fundraising-last-name"
              label={t("donationForm", "lastName")}
              placeholder={t("donationForm", "lastName")}
              error={!!errors.lastName?.message}
              helperText={errors.lastName?.message}
              required
              data-test="last-name-input"
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              name="email"
              rules={[
                isEmail(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
                isRequired(t("donationForm", "emailRequired")),
              ]}
              id="fundraising-email"
              type="email"
              label={t("donationForm", "email")}
              placeholder={t("donationForm", "email")}
              error={!!errors.email?.message}
              helperText={errors.email?.message}
              required
              data-test="email-input"
            />
          </Grid>
        </Grid>
      </Stack>
      {campaignAction === "CreateTeam" && (
        <Grid item xs={12}>
          <FormCheckbox
            control={control}
            name="joinTeamCreated"
            defaultChecked
            id="fundraising-create-team"
            label={
              <FormTypography variant="subtitle2">{t("donationForm", "campaignModal.joinTeamCreated")}</FormTypography>
            }
            size="small"
            data-test="join-team-created-checkbox"
          />
        </Grid>
      )}
    </Stack>
  );
};
